import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import FormError from '../../../FormError';
import Spinner from '../shared/Spinner';
import LoadFailure from '../shared/LoadFailure';
import { apiFetch } from '../shared/api';

export const MAPS_SCRIPT_ID = 'demo-add-google-maps';
const MAPS_READY_CALLBACK = '__rtfDemoAddGoogleMapsReady';

// Same widget and options as the legacy Add page: no bounds, so Google biases the
// suggestions by the browser's IP address itself. Only the place id is requested;
// the rest of the place comes from our own details endpoint.
const WIDGET_OPTIONS = { types: ['establishment'], fields: ['place_id'] };

let mapsLoader = null;

export function resetGoogleMapsLoader() {
    mapsLoader = null;
}

export function loadGoogleMaps(apiKey) {
    if (window.google?.maps?.places?.Autocomplete) {
        return Promise.resolve(window.google.maps);
    }

    if (!apiKey) {
        return Promise.reject(new Error('Google Maps API key is not configured.'));
    }

    if (!mapsLoader) {
        mapsLoader = new Promise((resolve, reject) => {
            window[MAPS_READY_CALLBACK] = () => {
                delete window[MAPS_READY_CALLBACK];
                resolve(window.google.maps);
            };

            const script = document.createElement('script');
            script.id = MAPS_SCRIPT_ID;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${MAPS_READY_CALLBACK}`;
            script.async = true;
            script.defer = true;
            script.onerror = () => {
                delete window[MAPS_READY_CALLBACK];
                script.remove();
                mapsLoader = null;
                reject(new Error('Google Maps failed to load.'));
            };
            document.head.appendChild(script);
        });
    }

    return mapsLoader;
}

const BusinessSearch = forwardRef(function BusinessSearch({ endpoints, mapsApiKey, onSelect, onLoadingChange, showInlineSpinner = true, disabled }, ref) {
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [failedPlaceId, setFailedPlaceId] = useState(null);
    const [attempt, setAttempt] = useState(0);
    const inputRef = useRef(null);
    const mountedRef = useRef(true);
    const selectRequestRef = useRef(0);

    useImperativeHandle(ref, () => ({
        focus: () => inputRef.current?.focus(),
    }), []);

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    const selectPlace = useCallback((placeId) => {
        const url = endpoints.details.replace('__PLACE_ID__', encodeURIComponent(placeId));

        selectRequestRef.current += 1;
        const requestId = selectRequestRef.current;
        const isStale = () => !mountedRef.current || requestId !== selectRequestRef.current;

        setLoadingDetails(true);
        setFailedPlaceId(null);
        setErrorMessage(null);
        onLoadingChange?.(true);

        apiFetch(url).then(({ status, json }) => {
            if (isStale()) {
                if (status < 200 || status >= 300) {
                    console.error(`superseded: business details lookup failed with status ${status}`);
                }
                return;
            }
            if (status >= 200 && status < 300 && json?.data) {
                setErrorMessage(null);
                if (inputRef.current) {
                    inputRef.current.value = '';
                }
                onSelect(json.data);
            } else if (status === 404) {
                setErrorMessage(__('Business not found. Please search again.'));
            } else {
                console.error(`Business details lookup failed with status ${status}`);
                setFailedPlaceId(placeId);
            }
        }).catch((error) => {
            if (isStale()) {
                console.error('superseded', error);
                return;
            }
            console.error(error);
            setFailedPlaceId(placeId);
        }).finally(() => {
            if (!isStale()) {
                setLoadingDetails(false);
                onLoadingChange?.(false);
            }
        });
    }, [endpoints.details, onSelect, onLoadingChange]);

    // The widget is bound to the input once per load; the listener reads the latest
    // handler through the ref so a parent re-render never binds a second widget.
    const selectPlaceRef = useRef(selectPlace);
    selectPlaceRef.current = selectPlace;

    useEffect(() => {
        let autocomplete = null;
        let maps = null;
        let cancelled = false;

        loadGoogleMaps(mapsApiKey).then((loadedMaps) => {
            if (cancelled || !inputRef.current) {
                return;
            }
            maps = loadedMaps;
            autocomplete = new maps.places.Autocomplete(inputRef.current, WIDGET_OPTIONS);
            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place?.place_id) {
                    selectPlaceRef.current(place.place_id);
                }
            });
            setErrorMessage(null);
        }).catch((error) => {
            console.error(error);
            if (!cancelled) {
                setErrorMessage(__('Business search is temporarily unavailable.'));
            }
        });

        return () => {
            cancelled = true;
            if (autocomplete && maps?.event?.clearInstanceListeners) {
                maps.event.clearInstanceListeners(autocomplete);
            }
        };
    }, [mapsApiKey, attempt]);

    const handleRetry = () => {
        setErrorMessage(null);
        setAttempt((value) => value + 1);
    };

    return (
        <div className="form-group demo-add-business-search" data-testid="demo-add-search">
            <label htmlFor="demo-add-search-input">{__('Search Business Name')}</label>
            <div className="input-group">
                <div className="input-group-prepend">
                    <span className="input-group-text"><i className="fas fa-search" aria-hidden="true" /></span>
                </div>
                <input
                    id="demo-add-search-input"
                    ref={inputRef}
                    type="text"
                    className="form-control"
                    aria-label={__('Search Business Name')}
                    placeholder={__('Search for your business...')}
                    disabled={disabled}
                    autoComplete="off"
                />
                {loadingDetails && showInlineSpinner &&
                    <div className="input-group-append">
                        <span className="input-group-text">
                            <Spinner label={__('Loading business details')} />
                        </span>
                    </div>
                }
            </div>

            {failedPlaceId && !loadingDetails &&
                <LoadFailure
                    message={__('Business details are temporarily unavailable.')}
                    onRetry={() => selectPlace(failedPlaceId)}
                />
            }

            {errorMessage &&
                <div className="demo-add-business-search-error">
                    <FormError error_message={errorMessage} />
                    <button type="button" className="btn btn-link btn-sm" onClick={handleRetry}>
                        {__('Retry')}
                    </button>
                </div>
            }
        </div>
    );
});

export default BusinessSearch;
